import {useContext} from 'react';

import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import {Grid, IconButton, styled, TableCell} from '@mui/material';

import {TableRowRequired} from '../../../../components/BasicTable/BasicTable';
import {BasicTableRow} from '../../../../components/BasicTable/BasicTableRow';
import {EmpionButton} from '../../../../components/EmpionButton/EmpionButton';
import {EmpionTooltip} from '../../../../components/EmpionTooltip/EmpionTooltip';
import {Icon, IconType} from '../../../../components/Icon/Icon';
import {TextOverflowWithTooltip} from '../../../../components/TextOverflowWithTooltip/TextOverflowWithTooltip';
import {LanguageContext} from '../../../../contexts/LanguageContext/LanguageContext';
import {useApiWrite} from '../../../../hooks/useApiWrite';
import {Post} from '../../../../lib/api/POST/Post';
import {PostRoutes} from '../../../../lib/api/POST/PostRoutes';
import {Campaign, CampaignStatus, getCampaignStatusTranslation} from '../../../../types/Campaign';
import {calculateDuration} from '../../../../utils/dateUtils/dateFormattingUtils';
import {toUrlSafeString} from '../../../../utils/toUrlSafeString';

const StyledCell = styled(TableCell)(() => ({
  paddingTop: '11px',
  paddingBottom: '11px',
  maxWidth: '210px',
}));

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator?.userAgent);

type Props = {
  campaign: Campaign;
  companyName: string;
} & TableRowRequired;

export const CampaignTableRow = ({campaign, companyName}: Props) => {
  const lang = useContext(LanguageContext);
  const {t} = useTranslation();
  const isLive = campaign.status === CampaignStatus.Live;
  const navigate = useNavigate();

  const onSuccess = (data: {token: string}) => {
    const url = `${process.env.REACT_APP_JOBS_BASE_URL}/draft/campaign/?id=${campaign.resourceId}&token=${data.token}`;
    if (isSafari) {
      window.location.href = url;
    } else {
      window.open(url, '_blank');
    }
  };

  const {makeRequest: generateToken} = useApiWrite<Post.GenerateCampaignToken, {token: string}>({
    method: 'post',
    path: PostRoutes[Post.GenerateCampaignToken](campaign.resourceId),
    onSuccess,
  });

  const onClick = () => {
    if (isLive) {
      const url = `${process.env.REACT_APP_JOBS_BASE_URL}/details/${campaign.resourceId}/${toUrlSafeString(
        companyName
      )}/${toUrlSafeString(campaign.name)}`;
      if (isSafari) {
        window.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    }
    return generateToken(undefined);
  };

  const viewAnalytics = () => {
    const url = `/company/campaigns/analytics/${campaign.resourceId}`;
    navigate(url);
  };

  return (
    <BasicTableRow>
      <StyledCell>
        <EmpionTooltip title={t('campaignsPage.analyticsTooltip')} placement='top' isEnabled>
          <IconButton onClick={viewAnalytics} name='analytics'>
            <AutoGraphIcon />
          </IconButton>
        </EmpionTooltip>
      </StyledCell>
      <StyledCell>
        <TextOverflowWithTooltip sxTypography={{fontSize: '14px'}} variant='body1' text={campaign.name} />
      </StyledCell>
      <StyledCell>
        <TextOverflowWithTooltip
          sxTypography={{fontSize: '14px'}}
          variant='body1'
          text={getCampaignStatusTranslation(campaign.status, lang)}
        />
      </StyledCell>
      <StyledCell>
        <TextOverflowWithTooltip
          sxTypography={{fontSize: '14px'}}
          variant='body1'
          text={calculateDuration(campaign?.dateRange?.startDate, campaign?.dateRange?.endDate, lang)}
        />
      </StyledCell>
      <StyledCell>
        <TextOverflowWithTooltip
          sxTypography={{fontSize: '14px'}}
          variant='body1'
          text={campaign?.matches?.total.toString()}
        />
      </StyledCell>
      <StyledCell>
        <Grid container justifyContent='flex-end'>
          <EmpionButton onClick={onClick} sx={{width: 'auto', minWidth: '32px', height: '32px', padding: 0}}>
            <Icon iconColor='currentColor' icon={IconType.Eyeball} />
          </EmpionButton>
        </Grid>
      </StyledCell>
    </BasicTableRow>
  );
};
